import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux'

import CartSummary from '../components/CartSummary';
import { reservationActive, getAdditionalFees, getServiceFee } from '../../../selectors/reservation';
import ContainerProps from './CartSummaryContainerComponent';
import { getOrderTotal } from '../../../selectors/cart'
import { isKioskMode, isCurrentRouterState } from '../../../selectors/ui';
import { getAllLoadedPriceLevels } from '../../ticket-picker/selectors/tickets';
import { isPyos, hasAppliedReserveCoupon } from '../../../selectors/reserved-seating';
import { withRouterCompat } from '../../../helpers/withRouterCompat';

class CartSummaryContainer extends Component<ContainerProps> {
    onEdit = () => {
        this.props.navigate(`/cart/${this.props.token}`);
    };

    onEditSeatSelection = () => {
        this.props.navigate(`/${this.props.listing}`);
    }

    render() {
        let props = {
            itemCount: this.props.itemCount,
            ticketTotal: this.props.ticketTotal,
            products: this.props.products,
            productSales: this.props.productSales,
            productCount: this.props.productCount,
            productTotal: this.props.productTotal,
            bundles: this.props.bundles,
            bundleSales: this.props.bundleSales,
            bundleCount: this.props.bundleCount,
            bundleTotal: this.props.bundleTotal,
            buyerServiceFee: this.props.buyerServiceFee,
            additionalFees: this.props.additionalFees ? this.props.additionalFees : [],
            totalCost: this.props.total,
            coupon: this.props.coupon,
            currencySymbol: this.props.currencySymbol,
            hasAutoCoupons: this.props.hasAutoCoupons,
            tickets: this.props.tickets,
            levels: this.props.levels,
            isKioskMode: this.props.isKioskMode,
            isTicketPicker: this.props.isTicketPicker,
            isPyos: this.props.isPyos,
            onEditSeatSelection: this.onEditSeatSelection,
            hasAppliedReserveCoupon: this.props.hasAppliedReserveCoupon,
            hideEventDateTime: this.props.hideEventDateTime,
        }

        return(
            <CartSummary
                {...props}
                onEdit={this.onEdit}
                editLink={this.props.editLink}
                locale={this.props.locale}
            />
        )
    }
}

const mapStateToProps = state => {
    //if viewing the cart summary on the ticket picker view of a kiosk, get values from
    //the ticketpicker, productpicker, and bundlepicker instead of the reservation so
    //that we can see them in real time.
    const isTicketPicker = isKioskMode(state) && !isCurrentRouterState('checkout')(state);

    const getTickets = state => {
        if (isTicketPicker) {
          let tickets = [];
          if (typeof state.ticketPicker.quantities === 'object' && Object.keys(state.ticketPicker.quantities).length) {
            Object.keys(state.ticketPicker.quantities).forEach(priceLevelId => {
              if (!isNaN(state.ticketPicker.quantities[priceLevelId])) {
                for (let i = 0; i < state.ticketPicker.quantities[priceLevelId]; i++) {
                  tickets.push({
                    price_level_id: priceLevelId,
                    cost: {amount: 0},
                  });
                }
              }
            });
          }
          return tickets;
        }
        return state.tickets.tickets;
    }
    
    const getProducts = state => {
      if (isTicketPicker) {
        let products = [];
        if (typeof state.productPicker.productsQuantities === 'object' && Object.keys(state.productPicker.productsQuantities).length) {
          Object.keys(state.productPicker.productsQuantities).forEach(productId => {
            if (!isNaN(state.productPicker.productsQuantities[productId])) {
              for (let i = 0; i < state.productPicker.productsQuantities[productId]; i++) {
                products.push({
                  product_id: productId, 
                  product_name: state.products.products[productId]?.name,
                  cost: {amount: 0},
                  buyer_fee: {amount: 0}
                });
              }
            }
          });
        }
        return products;
      }
      return state.productSales.products;
    }

    const getBundles = state => {
      if (isTicketPicker) {
        let bundles = [];
        if (typeof state.bundlePicker.bundleQuantities === 'object' && Object.keys(state.bundlePicker.bundleQuantities).length) {
          Object.keys(state.bundlePicker.bundleQuantities).forEach(bundleId => {
              if (!isNaN(state.bundlePicker.bundleQuantities[bundleId])) {
                for (let i = 0; i < state.bundlePicker.bundleQuantities[bundleId]; i++) {
                  bundles.push({
                    bundle_id: bundleId, 
                    name: state.bundles.bundles[bundleId]?.name,
                    cost: {amount: 0},
                    buyer_fee: {amount: 0}
                  });
                }
              }
          });
        }
        return bundles;
      }
      return state.bundleSales.bundles;
    }

    const getProductCount = state => {
        if (isTicketPicker) {
            let productCount = 0;
            if (typeof state.productPicker.productsQuantities === 'object' && Object.keys(state.productPicker.productsQuantities).length) {
                Object.keys(state.productPicker.productsQuantities).forEach(productId => {
                    const quantity = state.productPicker.productsQuantities[productId];
                    if (!isNaN(quantity)) {
                        productCount += quantity;
                    }
                });
            }
            return productCount;
        }
        return state.reservation.product_count;
    }

    const getBundleCount = state => {
        if (isTicketPicker) {
            let bundleCount = 0;
            if (typeof state.bundlePicker.bundleQuantities === 'object' && Object.keys(state.bundlePicker.bundleQuantities).length) {
                Object.keys(state.bundlePicker.bundleQuantities).forEach(bundleId => {
                    const quantity = state.bundlePicker.bundleQuantities[bundleId];
                    if (!isNaN(quantity)) {
                        bundleCount += quantity;
                    }
                });
            }
            return bundleCount;
        }
        return state.reservation.bundle_count;
    }

    return {
        active: reservationActive(state),
        itemCount: state.reservation.ticket_count,
        ticketTotal: state.reservation.ticket_total,
        productCount: getProductCount(state),
        productTotal: state.reservation.product_total,
        products: state.products.products,
        productSales: getProducts(state),
        bundles: state.bundles.bundles,
        bundleSales: getBundles(state),
        bundleCount: getBundleCount(state),
        bundleTotal: state.reservation.bundle_total,
        buyerServiceFee: getServiceFee(state),
        additionalFees: getAdditionalFees(state),
        fees: state.fees,
        token: state.reservation.token,
        coupon: state.coupon,
        currencySymbol: state.reservation.currency_symbol,
        hasAutoCoupons: (state.reservation.autoCouponAmount !== null),
        reservationSelectedDelivery: state.reservation.selected_delivery_option,
        reservationAdditionalFees: state.reservation.additional_fees,
        tickets: getTickets(state),
        levels: getAllLoadedPriceLevels(state),
        total: getOrderTotal(state),
        isKioskMode: isKioskMode(state),
        isTicketPicker: isTicketPicker,
        locale: state.intl.locale,
        isPyos: isPyos(state),
        listing: state.event.listing,
        hasAppliedReserveCoupon: hasAppliedReserveCoupon(state),
    }
};

CartSummaryContainer.propTypes = {
    active: PropTypes.bool,
    itemCount: PropTypes.number,
    ticketTotal: PropTypes.object,
    productCount: PropTypes.number,
    productTotal: PropTypes.object,
    bundleCount: PropTypes.number,
    bundleTotal: PropTypes.object,
    buyerServiceFee: PropTypes.object,
    additionalFees: PropTypes.array,
    totalCost: PropTypes.object,
    token: PropTypes.string,
    coupon: PropTypes.object,
    currencySymbol: PropTypes.string,
    hasAutoCoupons: PropTypes.bool,
    editLink: PropTypes.bool,
    hideEventDateTime: PropTypes.bool,
    navigate: PropTypes.func,
}

CartSummaryContainer.defaultProps = {
    editLink: true,
    hideEventDateTime: false,
}

export default connect(mapStateToProps, null)(CartSummaryContainer);
